import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import SecondaryLink_row from './seclinks_row';
import Pagination from '../../ui/pagination';
import { userService } from "../../../_services";
import EditUserLink from './Edit_Links_pop';
import AddUserLink from './Add_UserLink';
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Search, Link as LinkIcon, Loader2 } from "lucide-react";
import { ConfirmDialog } from "../../ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

/**
 * Secondary Links Management Page
 * Modernized with shadcn components - Manage secondary referral links
 */
function Links_Page() {
    const searchInputRef = useRef(null);

    // State
    const [linksData, setLinksData] = useState([]);
    const [allLinksData, setAllLinksData] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showAddLink, setShowAddLink] = useState(false);
    const [showInactivePopup, setShowInactivePopup] = useState(false);

    // Selected link data
    const [selectedLink, setSelectedLink] = useState({
        assigneduser: '',
        objectuid: '',
        linksuffix: '',
        groupnumber: '',
        groupnumberid: '',
        linktype: ''
    });

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getSecondaryLinks(userId, userName);
    }, []);

    const getSecondaryLinks = async (userId, userName) => {
        setLoading(true);
        try {
            const userRole = window.atob(localStorage.getItem('UserRole'));
            const apidata = await userService.GetSecondLink(userId, userName, userRole, '', 1, 100);
            if (apidata && apidata.length > 0) {
                setLinksData(apidata);
                setAllLinksData(apidata);
            }
        } catch (error) {
            console.error('Error fetching secondary links:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        const searchText = (value || searchInput).toLowerCase();
        
        if (!searchText) {
            setLinksData(allLinksData);
            return;
        }

        const filtered = allLinksData.filter((link) => {
            const userName = link.userName?.toLowerCase() || '';
            const groupNumber = link.groupNumber?.toLowerCase() || '';
            const linkSuffix = link.linkSuffix?.toLowerCase() || '';

            return (
                userName.includes(searchText) ||
                groupNumber.includes(searchText) ||
                linkSuffix.includes(searchText)
            );
        });

        setLinksData(filtered.length > 0 ? filtered : [{}]);
    };

    const handleEditLink = (event) => {
        if (event) {
            event.stopPropagation();
            const assignedUser = event.currentTarget.getAttribute('assigneduser');
            const objectUid = event.currentTarget.getAttribute('objectuid');
            const groupNumber = event.currentTarget.getAttribute('groupnumber');
            const groupNumberId = event.currentTarget.getAttribute('groupnumberid');
            const linkType = event.currentTarget.getAttribute('linktype');
            const linkSuffix = event.currentTarget.getAttribute('linksuffix');

            setSelectedLink({
                assigneduser: assignedUser,
                objectuid: objectUid,
                linksuffix: linkSuffix,
                groupnumber: groupNumber,
                groupnumberid: groupNumberId,
                linktype: linkType
            });
            setShowEditPopup(true);
        }
    };

    const closeEditLink = () => {
        setShowEditPopup(false);
        refreshLinksList();
    };

    const refreshLinksList = () => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        getSecondaryLinks(userId, userName);
    };

    const handleActivateDeactivate = async (event) => {
        const status = event.currentTarget.getAttribute('status');
        const repid = event.currentTarget.getAttribute('numbering');
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.UpdateUserStatus(userId, userName, repid, status);
            if (apidata && apidata.length > 0) {
                if (apidata[0].code === '1') {
                    refreshLinksList();
                } else if (apidata[0].code === '-2') {
                    setShowInactivePopup(true);
                }
            }
        } catch (error) {
            console.error('Error updating link status:', error);
        }
    };

    const onChangePage = (pageOfItems) => {
        setPageOfItems(pageOfItems);
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

    localStorage.setItem('pagename', 'linkspage');

    const dataToDisplay = searchInput.length ? linksData : (pageOfItems.length > 0 ? pageOfItems : linksData);

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-2xl font-semibold text-brand-dark">Secondary Links</h1>

            {/* Search and Add Button */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search links..."
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            handleSearch(e.target.value);
                        }}
                        disabled={loading}
                        className="pl-10"
                    />
                </div>

                <Button
                    onClick={() => setShowAddLink(true)}
                    disabled={loading}
                    variant="secondary"
                    className="w-full md:w-auto"
                >
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Add Links
                </Button>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
                        </div>
                    ) : dataToDisplay.length === 0 ? (
                        <div className="text-center py-12 text-brand-dark/60">
                            No Record Found
                        </div>
                    ) : (
                        <>
                            <div className="border rounded-md overflow-hidden">
                                <Table>
                                    <TableHeader className="bg-brand-light-1">
                                        <TableRow>
                                            <TableHead className="text-brand-dark">Assigned User</TableHead>
                                            <TableHead className="text-brand-dark">Group Number</TableHead>
                                            <TableHead className="text-brand-dark">Created On</TableHead>
                                            <TableHead className="text-brand-dark">Link Type</TableHead>
                                            <TableHead className="text-brand-dark">Link Suffix</TableHead>
                                            <TableHead className="text-brand-dark text-center">No. of Signups</TableHead>
                                            <TableHead className="text-brand-dark text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {dataToDisplay.map((linkRow, index) => (
                                            <SecondaryLink_row
                                                key={linkRow.objectUID || index}
                                                index={index}
                                                {...linkRow}
                                                toggle_add_edit_popup={handleEditLink}
                                                activate_deactivate={handleActivateDeactivate}
                                                setStateOfParent={(apidata) => {
                                                    setLinksData(apidata);
                                                    setAllLinksData(apidata);
                                                }}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            <div className="p-4 border-t">
                                <Pagination 
                                    items={linksData} 
                                    onChangePage={onChangePage} 
                                    showInfo={true}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Modals */}
            <ConfirmDialog
                open={showInactivePopup}
                onOpenChange={setShowInactivePopup}
                title="Inactive User"
                description="This user needs to verify their email address before their account can be activated."
                onConfirm={() => setShowInactivePopup(false)}
                confirmText="Ok"
                showIcon={false}
            />

            <Dialog open={showEditPopup} onOpenChange={setShowEditPopup}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Secondary Link</DialogTitle>
                    </DialogHeader>
                    <EditUserLink 
                        assigneduser={selectedLink.assigneduser}
                        groupnumberid={selectedLink.groupnumberid}
                        linktype={selectedLink.linktype}
                        linksuffix={selectedLink.linksuffix}
                        objectuid={selectedLink.objectuid}
                        groupnumber={selectedLink.groupnumber}
                        toggle_add_edit_popup={closeEditLink}
                        updateUserList={refreshLinksList}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Add Secondary Link</DialogTitle>
                    </DialogHeader>
                    <AddUserLink 
                        adduserclose={() => setShowAddLink(false)}
                        updateUserList={refreshLinksList}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Links_Page;
